/**
 * App-level SegmentedSelector — wraps a3s-ui SegmentedControl with the app's
 * Icon component for backward compatibility.
 */
import { type FC, memo } from "react";
import { SegmentedControl, type SegmentedControlProps, type TextColorPalette } from "@visioni/a3s-ui/components";
import { Icon, type IconNameType } from "@src/components/Icon";
import { useUnistyles } from "react-native-unistyles";
import { sizes } from "@src/constants/sizes";
import type { StyleProp, ViewStyle } from "react-native";
import type { AnimatedStyle } from "react-native-reanimated";

type SegmentedSelectorProps = {
  optionsLabels: {
    title: string;
    icon?: IconNameType;
  }[];
  selectedOption: string;
  onSelectOption: (option: string) => void;
  width?: number;
  isSelectionVisible?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  activeBoxStyle?: AnimatedStyle<ViewStyle>;
  textColorActiveBox?: TextColorPalette;
};

const SegmentedSelector: FC<SegmentedSelectorProps> = memo(({ optionsLabels, ...rest }) => {
  const { theme } = useUnistyles();
  const { selectedOption, textColorActiveBox } = rest;
  const iconColor = textColorActiveBox ? textColorActiveBox : theme.colors.text;

  // Map the app's option format (with IconNameType) to a3s-ui's format (with ReactNode)
  const options: SegmentedControlProps["options"] = optionsLabels.map((opt) => ({
    title: opt.title,
    icon:
      opt.icon ?
        <Icon name={opt.icon} size={20} color={opt.title === selectedOption ? iconColor : theme.colors.textMuted} />
      : undefined,
  }));

  return (
    <SegmentedControl
      options={options}
      maxWidth={sizes.maxWidthWeb}
      textColorActiveBox={textColorActiveBox}
      {...rest}
    />
  );
});

SegmentedSelector.displayName = "SegmentedSelector";

export default SegmentedSelector;
